import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, GripVertical, Plus, Trash2, RotateCcw, Save, Loader2, Eye, EyeOff, PlusCircle } from 'lucide-react';
import api from '../../../services/api';

// All available sections that can be added to menus
const AVAILABLE_SECTIONS = {
  business: [
    { id: 'home', label: 'Home', section: 'home' },
    { id: 'about', label: 'About Us', section: 'about' },
    { id: 'services', label: 'Services', section: 'services' },
    { id: 'how_it_works', label: 'How It Works', section: 'how_it_works' },
    { id: 'why_choose_us', label: 'Why Choose Us', section: 'why_choose_us' },
    { id: 'stats', label: 'Stats', section: 'stats' },
    { id: 'legacy', label: 'Our Legacy', section: 'legacy' },
    { id: 'team', label: 'Our Team', section: 'team' },
    { id: 'clients', label: 'Our Clients', section: 'clients' },
    { id: 'gallery', label: 'Gallery', section: 'gallery' },
    { id: 'testimonials', label: 'Testimonials', section: 'testimonials' },
    { id: 'faq', label: 'FAQ', section: 'faq' },
    { id: 'pricing', label: 'Pricing', section: 'pricing' },
    { id: 'projects', label: 'Projects', section: 'projects' },
    { id: 'blogs', label: 'Blogs', section: 'blogs' },
    { id: 'locations', label: 'Locations', section: 'locations' },
    { id: 'contact', label: 'Contact', section: 'contact' },
  ],
  portfolio: [
    { id: 'home', label: 'Home', section: 'home' },
    { id: 'profile', label: 'Profile', section: 'profile' },
    { id: 'education', label: 'Education', section: 'education' },
    { id: 'career', label: 'Career', section: 'career' },
    { id: 'skills', label: 'Skills', section: 'skills' },
    { id: 'awards', label: 'Awards', section: 'awards' },
    { id: 'projects', label: 'Projects', section: 'projects' },
    { id: 'testimonials', label: 'Testimonials', section: 'testimonials' },
    { id: 'blogs', label: 'Blogs', section: 'blogs' },
    { id: 'contact', label: 'Contact', section: 'contact' },
  ],
  freelancer: [
    { id: 'home', label: 'Home', section: 'home' },
    { id: 'profile', label: 'Profile', section: 'profile' },
    { id: 'services', label: 'Services', section: 'services' },
    { id: 'how_it_works', label: 'How It Works', section: 'how_it_works' },
    { id: 'portfolio', label: 'Portfolio', section: 'portfolio' },
    { id: 'skills', label: 'Skills', section: 'skills' },
    { id: 'testimonials', label: 'Testimonials', section: 'testimonials' },
    { id: 'clients', label: 'Clients', section: 'clients' },
    { id: 'pricing', label: 'Pricing', section: 'pricing' },
    { id: 'faq', label: 'FAQ', section: 'faq' },
    { id: 'blogs', label: 'Blogs', section: 'blogs' },
    { id: 'contact', label: 'Contact', section: 'contact' },
  ],
};

const MenuEditor = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isCustomized, setIsCustomized] = useState(false);
  const [websiteType, setWebsiteType] = useState('portfolio');
  const [expandedItems, setExpandedItems] = useState({});
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addToDropdown, setAddToDropdown] = useState(null); // null = top level, or parent id

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    try {
      const res = await api.get('/business/menu/');
      // Ensure all items have a 'visible' property
      const items = (res.data.menu_items || []).map(item => ({
        ...item,
        visible: item.visible !== false, // default to true
        children: (item.children || []).map(child => ({
          ...child,
          visible: child.visible !== false,
        })),
      }));
      setMenuItems(items);
      setIsCustomized(res.data.is_customized || false);
      setWebsiteType(res.data.website_type || 'portfolio');
    } catch (err) {
      console.error('Error fetching menu:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveMenu = async () => {
    setSaving(true);
    try {
      await api.put('/business/menu/', { menu_items: menuItems });
      setIsCustomized(true);
    } catch (err) {
      console.error('Error saving menu:', err);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm('Reset menu to default? Your customizations will be lost.')) return;

    setSaving(true);
    try {
      const res = await api.post('/business/menu/reset/');
      setMenuItems(res.data.menu_items || []);
      setIsCustomized(false);
    } catch (err) {
      console.error('Error resetting menu:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateItemLabel = (itemId, newLabel) => {
    setMenuItems(items =>
      items.map(item =>
        item.id === itemId ? { ...item, label: newLabel } : item
      )
    );
  };

  const updateChildLabel = (parentId, childId, newLabel) => {
    setMenuItems(items =>
      items.map(item =>
        item.id === parentId
          ? {
              ...item,
              children: item.children.map(child =>
                child.id === childId ? { ...child, label: newLabel } : child
              ),
            }
          : item
      )
    );
  };

  const toggleItemVisibility = (itemId) => {
    setMenuItems(items =>
      items.map(item =>
        item.id === itemId ? { ...item, visible: !item.visible } : item
      )
    );
  };

  const toggleChildVisibility = (parentId, childId) => {
    setMenuItems(items =>
      items.map(item =>
        item.id === parentId
          ? {
              ...item,
              children: item.children.map(child =>
                child.id === childId ? { ...child, visible: !child.visible } : child
              ),
            }
          : item
      )
    );
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const moveItem = (index, direction) => {
    const newItems = [...menuItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;

    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    setMenuItems(newItems);
  };

  const removeItem = (itemId) => {
    if (!window.confirm('Remove this menu item? You can add it back later.')) return;
    setMenuItems(items => items.filter(item => item.id !== itemId));
  };

  const removeChild = (parentId, childId) => {
    if (!window.confirm('Remove this submenu item? You can add it back later.')) return;
    setMenuItems(items =>
      items.map(item =>
        item.id === parentId
          ? { ...item, children: item.children.filter(c => c.id !== childId) }
          : item
      )
    );
  };

  const moveChildToParent = (fromParentId, childId, toParentId) => {
    setMenuItems(items => {
      const fromParent = items.find(p => p.id === fromParentId);
      const childToMove = fromParent?.children?.find(c => c.id === childId);
      if (!childToMove) return items;

      return items.map(item => {
        if (item.id === fromParentId) {
          return {
            ...item,
            children: item.children.filter(c => c.id !== childId)
          };
        } else if (item.id === toParentId) {
          return {
            ...item,
            children: [...(item.children || []), childToMove]
          };
        }
        return item;
      });
    });
  };

  const moveChildUp = (parentId, childIndex) => {
    if (childIndex === 0) return;
    setMenuItems(items =>
      items.map(item => {
        if (item.id === parentId && item.children) {
          const newChildren = [...item.children];
          [newChildren[childIndex - 1], newChildren[childIndex]] =
            [newChildren[childIndex], newChildren[childIndex - 1]];
          return { ...item, children: newChildren };
        }
        return item;
      })
    );
  };

  const moveChildDown = (parentId, childIndex, totalChildren) => {
    if (childIndex >= totalChildren - 1) return;
    setMenuItems(items =>
      items.map(item => {
        if (item.id === parentId && item.children) {
          const newChildren = [...item.children];
          [newChildren[childIndex], newChildren[childIndex + 1]] =
            [newChildren[childIndex + 1], newChildren[childIndex]];
          return { ...item, children: newChildren };
        }
        return item;
      })
    );
  };

  const getDropdownParents = () => {
    return menuItems.filter(item => item.children !== undefined);
  };

  // Get sections that are NOT currently in the menu
  const getAvailableSections = () => {
    const availableForType = AVAILABLE_SECTIONS[websiteType] || AVAILABLE_SECTIONS.business;
    const usedSections = new Set();

    // Collect all used section ids
    menuItems.forEach(item => {
      if (item.section) usedSections.add(item.section);
      if (item.children) {
        item.children.forEach(child => {
          if (child.section) usedSections.add(child.section);
        });
      }
    });

    return availableForType.filter(s => !usedSections.has(s.section));
  };

  // Add a new item to top level
  const addTopLevelItem = (sectionData) => {
    const newItem = {
      id: sectionData.id + '_' + Date.now(),
      label: sectionData.label,
      section: sectionData.section,
      visible: true,
      children: [],
    };
    setMenuItems([...menuItems, newItem]);
    setShowAddPanel(false);
  };

  // Add a new item to a dropdown
  const addToDropdownItem = (parentId, sectionData) => {
    const newChild = {
      id: sectionData.id + '_' + Date.now(),
      label: sectionData.label,
      section: sectionData.section,
      visible: true,
    };
    setMenuItems(items =>
      items.map(item =>
        item.id === parentId
          ? { ...item, children: [...(item.children || []), newChild] }
          : item
      )
    );
    setAddToDropdown(null);
  };

  // Add a new empty dropdown
  const addNewDropdown = () => {
    const newDropdown = {
      id: 'dropdown_' + Date.now(),
      label: 'New Menu',
      section: null,
      visible: true,
      children: [],
    };
    setMenuItems([...menuItems, newDropdown]);
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Loader2 className="animate-spin" style={{ color: '#6366f1', margin: '0 auto' }} size={32} />
      </div>
    );
  }

  const availableSections = getAvailableSections();

  return (
    <div style={{ padding: '1.5rem', backgroundColor: '#1e293b', borderRadius: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h3 style={{ color: '#fff', fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
            Navigation Menu
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            Customize your website's navigation structure
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {isCustomized && (
            <button
              onClick={resetToDefault}
              disabled={saving}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                border: '1px solid rgba(255,255,255,0.1)',
                backgroundColor: 'transparent',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              <RotateCcw size={14} />
              Reset
            </button>
          )}
          <button
            onClick={saveMenu}
            disabled={saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: 'none',
              backgroundColor: '#6366f1',
              color: '#fff',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Add Item Buttons */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => setShowAddPanel(!showAddPanel)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            border: '1px dashed rgba(99, 102, 241, 0.5)',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            color: '#818cf8',
            fontSize: '0.875rem',
            cursor: 'pointer',
          }}
        >
          <PlusCircle size={16} />
          Add Menu Item
        </button>
        <button
          onClick={addNewDropdown}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            border: '1px dashed rgba(34, 197, 94, 0.5)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            color: '#4ade80',
            fontSize: '0.875rem',
            cursor: 'pointer',
          }}
        >
          <Plus size={16} />
          Add Dropdown Menu
        </button>
      </div>

      {/* Add Item Panel */}
      {showAddPanel && (
        <div style={{
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: '#0f172a',
          borderRadius: '0.5rem',
          border: '1px solid rgba(99, 102, 241, 0.3)',
        }}>
          <p style={{ color: '#fff', fontSize: '0.875rem', marginBottom: '0.75rem', fontWeight: '500' }}>
            Select a section to add:
          </p>
          {availableSections.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {availableSections.map(section => (
                <button
                  key={section.id}
                  onClick={() => addTopLevelItem(section)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid rgba(255,255,255,0.1)',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: '#fff',
                    fontSize: '0.8125rem',
                    cursor: 'pointer',
                  }}
                >
                  + {section.label}
                </button>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8125rem' }}>
              All sections are already in the menu.
            </p>
          )}
          <button
            onClick={() => setShowAddPanel(false)}
            style={{
              marginTop: '0.75rem',
              padding: '0.375rem 0.75rem',
              borderRadius: '0.25rem',
              border: 'none',
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.75rem',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {menuItems.map((item, index) => {
          const hasChildren = item.children && item.children.length > 0;
          const isDropdown = item.children !== undefined;
          const isExpanded = expandedItems[item.id];
          const isHidden = !item.visible;

          return (
            <div
              key={item.id}
              style={{
                backgroundColor: '#0f172a',
                borderRadius: '0.5rem',
                border: `1px solid ${isHidden ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                overflow: 'hidden',
                opacity: isHidden ? 0.6 : 1,
              }}
            >
              {/* Main Item */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  gap: '0.75rem',
                }}
              >
                <GripVertical size={16} style={{ color: 'rgba(255,255,255,0.3)', cursor: 'grab' }} />

                {/* Visibility Toggle */}
                <button
                  onClick={() => toggleItemVisibility(item.id)}
                  title={item.visible ? 'Hide from menu' : 'Show in menu'}
                  style={{
                    padding: '0.375rem',
                    borderRadius: '0.25rem',
                    border: 'none',
                    backgroundColor: item.visible ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                    color: item.visible ? '#4ade80' : '#f87171',
                    cursor: 'pointer',
                  }}
                >
                  {item.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>

                <input
                  type="text"
                  value={item.label}
                  onChange={(e) => updateItemLabel(item.id, e.target.value)}
                  style={{
                    flex: 1,
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid rgba(255,255,255,0.1)',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: '#fff',
                    fontSize: '0.875rem',
                  }}
                />

                {item.highlight && (
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    backgroundColor: 'rgba(99, 102, 241, 0.2)',
                    color: '#818cf8',
                    fontSize: '0.75rem',
                  }}>
                    CTA
                  </span>
                )}

                {isHidden && (
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    backgroundColor: 'rgba(239, 68, 68, 0.2)',
                    color: '#f87171',
                    fontSize: '0.75rem',
                  }}>
                    Hidden
                  </span>
                )}

                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <button
                    onClick={() => moveItem(index, 'up')}
                    disabled={index === 0}
                    style={{
                      padding: '0.375rem',
                      borderRadius: '0.25rem',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: index === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.5)',
                      cursor: index === 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    onClick={() => moveItem(index, 'down')}
                    disabled={index === menuItems.length - 1}
                    style={{
                      padding: '0.375rem',
                      borderRadius: '0.25rem',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: index === menuItems.length - 1 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.5)',
                      cursor: index === menuItems.length - 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <ChevronDown size={16} />
                  </button>
                </div>

                {isDropdown && (
                  <button
                    onClick={() => toggleExpand(item.id)}
                    style={{
                      padding: '0.375rem',
                      borderRadius: '0.25rem',
                      border: 'none',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      color: 'rgba(255,255,255,0.7)',
                      cursor: 'pointer',
                    }}
                  >
                    <ChevronDown
                      size={16}
                      style={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </button>
                )}

                <button
                  onClick={() => removeItem(item.id)}
                  title="Remove item"
                  style={{
                    padding: '0.375rem',
                    borderRadius: '0.25rem',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: '#ef4444',
                    cursor: 'pointer',
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {/* Children */}
              {isDropdown && isExpanded && (
                <div style={{
                  borderTop: '1px solid rgba(255,255,255,0.05)',
                  padding: '0.5rem 1rem 0.75rem 2.5rem',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <p style={{
                      color: 'rgba(255,255,255,0.4)',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      margin: 0,
                    }}>
                      Dropdown Items
                    </p>
                    <button
                      onClick={() => setAddToDropdown(addToDropdown === item.id ? null : item.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        border: '1px dashed rgba(99, 102, 241, 0.5)',
                        backgroundColor: 'transparent',
                        color: '#818cf8',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                      }}
                    >
                      <Plus size={12} />
                      Add Item
                    </button>
                  </div>

                  {/* Add to dropdown panel */}
                  {addToDropdown === item.id && (
                    <div style={{
                      marginBottom: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                      borderRadius: '0.375rem',
                      border: '1px solid rgba(99, 102, 241, 0.2)',
                    }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                        {availableSections.length > 0 ? (
                          availableSections.map(section => (
                            <button
                              key={section.id}
                              onClick={() => addToDropdownItem(item.id, section)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.25rem',
                                border: 'none',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                color: '#fff',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                              }}
                            >
                              + {section.label}
                            </button>
                          ))
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                            All sections are in use
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {item.children && item.children.map((child, childIndex) => {
                    const childHidden = !child.visible;
                    return (
                      <div
                        key={child.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.375rem 0',
                          opacity: childHidden ? 0.5 : 1,
                        }}
                      >
                        {/* Child Visibility Toggle */}
                        <button
                          onClick={() => toggleChildVisibility(item.id, child.id)}
                          title={child.visible ? 'Hide' : 'Show'}
                          style={{
                            padding: '0.25rem',
                            borderRadius: '0.25rem',
                            border: 'none',
                            backgroundColor: child.visible ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                            color: child.visible ? '#4ade80' : '#f87171',
                            cursor: 'pointer',
                          }}
                        >
                          {child.visible ? <Eye size={12} /> : <EyeOff size={12} />}
                        </button>

                        <input
                          type="text"
                          value={child.label}
                          onChange={(e) => updateChildLabel(item.id, child.id, e.target.value)}
                          style={{
                            flex: 1,
                            padding: '0.375rem 0.625rem',
                            borderRadius: '0.25rem',
                            border: '1px solid rgba(255,255,255,0.1)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            color: '#fff',
                            fontSize: '0.8125rem',
                          }}
                        />
                        <span style={{
                          color: 'rgba(255,255,255,0.3)',
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                        }}>
                          → {child.section}
                        </span>

                        {childHidden && (
                          <span style={{
                            padding: '0.125rem 0.375rem',
                            borderRadius: '0.25rem',
                            backgroundColor: 'rgba(239, 68, 68, 0.2)',
                            color: '#f87171',
                            fontSize: '0.625rem',
                          }}>
                            Hidden
                          </span>
                        )}

                        {/* Move up/down */}
                        <div style={{ display: 'flex', gap: '0.125rem' }}>
                          <button
                            onClick={() => moveChildUp(item.id, childIndex)}
                            disabled={childIndex === 0}
                            style={{
                              padding: '0.25rem',
                              borderRadius: '0.25rem',
                              border: 'none',
                              backgroundColor: 'transparent',
                              color: childIndex === 0 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.4)',
                              cursor: childIndex === 0 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            onClick={() => moveChildDown(item.id, childIndex, item.children.length)}
                            disabled={childIndex >= item.children.length - 1}
                            style={{
                              padding: '0.25rem',
                              borderRadius: '0.25rem',
                              border: 'none',
                              backgroundColor: 'transparent',
                              color: childIndex >= item.children.length - 1 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.4)',
                              cursor: childIndex >= item.children.length - 1 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>

                        {/* Move to different dropdown */}
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              moveChildToParent(item.id, child.id, e.target.value);
                            }
                          }}
                          title="Move to another dropdown"
                          style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            border: '1px solid rgba(255,255,255,0.1)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            color: 'rgba(255,255,255,0.6)',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                          }}
                        >
                          <option value="">Move to...</option>
                          {getDropdownParents()
                            .filter(p => p.id !== item.id)
                            .map(parent => (
                              <option key={parent.id} value={parent.id}>
                                {parent.label}
                              </option>
                            ))
                          }
                        </select>

                        <button
                          onClick={() => removeChild(item.id, child.id)}
                          style={{
                            padding: '0.25rem',
                            borderRadius: '0.25rem',
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: '#ef4444',
                            cursor: 'pointer',
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                  {(!item.children || item.children.length === 0) && (
                    <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8125rem', fontStyle: 'italic' }}>
                      No items in this dropdown. Click "Add Item" above.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{
        marginTop: '1rem',
        padding: '0.75rem',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        borderRadius: '0.5rem',
        border: '1px solid rgba(99, 102, 241, 0.2)',
      }}>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8125rem', margin: 0 }}>
          <strong style={{ color: '#818cf8' }}>Tips:</strong>
          <br />• Click <Eye size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> to show/hide items (hidden items won't appear on your site)
          <br />• Use "Move to..." to transfer items between dropdowns
          <br />• Click "Add Menu Item" to add sections you've removed
          <br />• Don't forget to click "Save Changes"!
        </p>
      </div>
    </div>
  );
};

export default MenuEditor;
